import {
    useCallback,
    useMemo,
} from 'react'
import {
    useGridApiRef,
} from '@mui/x-data-grid-pro'

import useDataGridSource from 'app/shared-components/SkyNetMasterDataGrid/hooks/useDataGridSource'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useLabel from 'app/hooks/useLabel'
import longTermLeaseInvoiceRequests from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.requests'
import {
    PersistentKeys,
} from 'app/enums/persistentKeys'
import {
    ContractCurrency,
} from 'app/enums/label.enums'
import SkyNetDataGridExpandable from 'app/shared-components/SkyNetDataGridExpandable'
import {
    ExtendedGridApiPro,
} from 'app/shared-components/SkyBaseDataGrid'

import useArchiveLongTermLeasePosition
    from 'app/requests/LongTermLeasePosition/hooks/useArchiveLongTermLeasePosition'
import {
    LongTermLeaseInvoicePositionContext,
} from './hooks/useLongTermLeaseInvoicePositionContext'
import LongTermLeaseInvoicePositionCreate from './LongTermLeaseInvoicePositionCreate'
import LongTermLeaseInvoicePositionUpdate from './LongTermLeaseInvoicePositionUpdate'
import getColumns from './longTermLeaseInvoicePosition.columns'

const persistentKey = PersistentKeys.LongTermLeaseInvoicePositionTable

const LongTermLeaseInvoicePosition = ({
    longTermLeaseInvoiceId,
    currency,
    className,
    disabled = false,
}: Readonly<{
    longTermLeaseInvoiceId: number,
    currency: ContractCurrency,
    className: string,
    disabled?: boolean,
}>) => {
    const l = useLabel()
    const apiRef = useGridApiRef<ExtendedGridApiPro>()

    const dataSource = useDataGridSource({
        request: longTermLeaseInvoiceRequests.positionFilterRequestAll({
            longTermLeaseInvoiceId,
        }),
        key: RequestKeys.getLongTermLeaseInvoicePositionFilterRequest,
    })

    const refetch = useCallback(async () => {
        dataSource.invalidate()
        await apiRef?.current?.dataSource?.fetchRows()
    }, [
        apiRef,
        dataSource,
    ])

    const {
        mutate: archiveLongTermLeasePosition,
    } = useArchiveLongTermLeasePosition({
        onSuccess: refetch,
    })

    const contextValue = useMemo(() => {
        return {
            longTermLeaseInvoiceId,
        }
    }, [longTermLeaseInvoiceId])

    const handleDeleteRow = useCallback((id: number) => {
        archiveLongTermLeasePosition({
            longTermLeasePositionId: id,
        })
    }, [archiveLongTermLeasePosition])

    const columns = getColumns({
        currency,
        disabled,
        handleDeleteRow,
    })

    return (
        <LongTermLeaseInvoicePositionContext.Provider value={contextValue}>
            <div className={className}>
                <SkyNetDataGridExpandable
                    gridApiRef={apiRef}
                    persistentKey={persistentKey}
                    customDataSource={dataSource}
                    columns={columns}
                    toolbarProps={{
                        title: l('POSITIONS'),
                    }}
                    ActionComponents={{
                        Create: {
                            component: LongTermLeaseInvoicePositionCreate,
                        },
                        Update: {
                            component: LongTermLeaseInvoicePositionUpdate,
                        },
                    }}
                    disabled={disabled}
                />
            </div>
        </LongTermLeaseInvoicePositionContext.Provider>
    )
}

export default LongTermLeaseInvoicePosition
